import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download, Send, XCircle, CreditCard, Copy, CheckCircle, Clock, AlertTriangle, Edit, Save, X, Plus, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { billingAPI } from '../lib/api';
import { toast } from 'sonner';

const statusConfig = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-800', icon: Clock },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-800', icon: Send },
  partially_paid: { label: 'Partially Paid', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-500', icon: XCircle },
};

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  // Edit mode
  const [isEditing, setIsEditing] = useState(searchParams.get('edit') === 'true');
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    payment_method: 'bank_transfer',
    transaction_reference: '',
    notes: ''
  });
  
  const [cancelReason, setCancelReason] = useState('');

  const fetchInvoice = async () => {
    try {
      const { data } = await billingAPI.getInvoice(id);
      setInvoice(data);
      setPaymentData(prev => ({ ...prev, amount: data.balance_due }));
      // Initialize edit form with invoice data
      setEditForm({
        customer_name: data.customer_name || '',
        customer_email: data.customer_email || '',
        customer_phone: data.customer_phone || '',
        customer_address: data.customer_address || '',
        customer_billing_address: data.customer_billing_address || '',
        customer_gstin: data.customer_gstin || '',
        customer_state_code: data.customer_state_code || '',
        gst_applicable: data.gst_applicable !== false,  // Default to true
        invoice_date: data.invoice_date?.slice(0, 10) || '',
        due_date: data.due_date?.slice(0, 10) || '',
        currency: data.currency || 'INR',
        notes: data.notes || '',
        terms: data.terms || '',
        items: data.items || data.line_items || []
      });
    } catch (error) {
      toast.error('Failed to load invoice');
      navigate('/billing/invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  const handleDownloadPDF = async () => {
    try {
      const response = await billingAPI.downloadInvoicePDF(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${invoice.invoice_number.replace(/\//g, '-')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleSendInvoice = async () => {
    setSending(true);
    try {
      await billingAPI.sendInvoice(id);
      toast.success('Invoice sent successfully');
      setSendDialogOpen(false);
      fetchInvoice();
    } catch (error) {
      toast.error('Failed to send invoice');
    } finally {
      setSending(false);
    }
  };

  const handleRecordPayment = async () => {
    if (paymentData.amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setProcessing(true);
    try {
      await billingAPI.markPaid(id, paymentData);
      toast.success('Payment recorded successfully');
      setPaymentDialogOpen(false);
      fetchInvoice();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelInvoice = async () => {
    setProcessing(true);
    try {
      await billingAPI.cancelInvoice(id, cancelReason);
      toast.success('Invoice cancelled');
      setCancelDialogOpen(false);
      fetchInvoice();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel invoice');
    } finally {
      setProcessing(false);
    }
  };

  const copyPaymentLink = () => {
    const link = `${window.location.origin}/pay/${invoice.id}`;
    navigator.clipboard.writeText(link);
    toast.success('Payment link copied to clipboard');
  };

  // Edit handlers
  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing - reset form
      setEditForm({
        customer_name: invoice.customer_name || '',
        customer_email: invoice.customer_email || '',
        customer_phone: invoice.customer_phone || '',
        customer_address: invoice.customer_address || '',
        customer_gstin: invoice.customer_gstin || '',
        invoice_date: invoice.invoice_date?.slice(0, 10) || '',
        due_date: invoice.due_date?.slice(0, 10) || '',
        currency: invoice.currency || 'INR',
        notes: invoice.notes || '',
        terms: invoice.terms || '',
        items: invoice.items || []
      });
    }
    setIsEditing(!isEditing);
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      // Calculate totals
      const subtotal = editForm.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
      const taxAmount = editForm.items.reduce((sum, item) => {
        const itemTotal = item.quantity * item.unit_price;
        return sum + (itemTotal * (item.tax_rate || 0) / 100);
      }, 0);
      const totalAmount = subtotal + taxAmount;

      const updateData = {
        ...editForm,
        subtotal,
        tax_amount: taxAmount,
        total_amount: totalAmount,
        balance_due: totalAmount - (invoice.amount_paid || 0)
      };

      await billingAPI.updateInvoice(id, updateData);
      toast.success('Invoice updated successfully');
      setIsEditing(false);
      fetchInvoice();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update invoice');
    } finally {
      setSaving(false);
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...editForm.items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Recalculate item amount
    if (field === 'quantity' || field === 'unit_price') {
      const qty = field === 'quantity' ? parseFloat(value) || 0 : newItems[index].quantity || 0;
      const price = field === 'unit_price' ? parseFloat(value) || 0 : newItems[index].unit_price || 0;
      newItems[index].amount = qty * price;
    }
    
    setEditForm({ ...editForm, items: newItems });
  };

  const handleAddItem = () => {
    setEditForm({
      ...editForm,
      items: [...editForm.items, { description: '', quantity: 1, unit_price: 0, amount: 0, tax_rate: 18 }]
    });
  };

  const handleRemoveItem = (index) => {
    const newItems = editForm.items.filter((_, i) => i !== index);
    setEditForm({ ...editForm, items: newItems });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!invoice) return null;

  const config = statusConfig[invoice.status] || statusConfig.draft;
  const StatusIcon = config.icon;
  const canEdit = ['draft', 'sent'].includes(invoice.status);

  // Calculate totals for edit mode
  const editSubtotal = editForm.items?.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0) || 0;
  const editTaxAmount = editForm.items?.reduce((sum, item) => {
    const itemTotal = item.quantity * item.unit_price;
    return sum + (itemTotal * (item.tax_rate || 0) / 100);
  }, 0) || 0;
  const editTotal = editSubtotal + editTaxAmount;

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/billing/invoices')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{invoice.invoice_number}</h1>
            <p className="text-muted-foreground">{invoice.customer_name}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge className={`${config.color} flex items-center gap-1`}>
            <StatusIcon className="h-3 w-3" />
            {config.label}
          </Badge>
          
          {/* Edit/Save/Cancel buttons */}
          {canEdit && !isEditing && (
            <Button variant="outline" size="sm" onClick={handleEditToggle} data-testid="edit-invoice-btn">
              <Edit className="h-4 w-4 mr-1" /> Edit
            </Button>
          )}
          {isEditing && (
            <>
              <Button size="sm" onClick={handleSaveChanges} disabled={saving} data-testid="save-invoice-btn">
                {saving ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : <Save className="h-4 w-4 mr-1" />}
                Save
              </Button>
              <Button variant="outline" size="sm" onClick={handleEditToggle}>
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
            </>
          )}
          
          <Button variant="outline" size="sm" onClick={handleDownloadPDF}>
            <Download className="h-4 w-4 mr-1" /> PDF
          </Button>
          {invoice.status === 'draft' && !isEditing && (
            <Button size="sm" onClick={() => setSendDialogOpen(true)}>
              <Send className="h-4 w-4 mr-1" /> Send
            </Button>
          )}
          {['sent', 'partially_paid', 'overdue'].includes(invoice.status) && !isEditing && (
            <>
              <Button size="sm" onClick={() => setPaymentDialogOpen(true)}>
                <CreditCard className="h-4 w-4 mr-1" /> Record Payment
              </Button>
              <Button variant="outline" size="sm" onClick={copyPaymentLink}>
                <Copy className="h-4 w-4 mr-1" /> Copy Payment Link
              </Button>
            </>
          )}
          {['draft', 'sent'].includes(invoice.status) && !isEditing && (
            <Button variant="destructive" size="sm" onClick={() => setCancelDialogOpen(true)}>
              <XCircle className="h-4 w-4 mr-1" /> Cancel
            </Button>
          )}
        </div>
      </div>

      {/* Invoice Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Total Amount</p>
            <p className="text-2xl font-bold">₹{(isEditing ? editTotal : invoice.total_amount)?.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Amount Paid</p>
            <p className="text-2xl font-bold text-green-600">₹{invoice.amount_paid?.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Balance Due</p>
            <p className="text-2xl font-bold text-orange-600">₹{(isEditing ? editTotal - (invoice.amount_paid || 0) : invoice.balance_due)?.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Due Date</p>
            {isEditing ? (
              <Input 
                type="date" 
                value={editForm.due_date} 
                onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })}
                className="mt-1"
                data-testid="edit-due-date"
              />
            ) : (
              <p className="text-2xl font-bold">{invoice.due_date?.slice(0, 10)}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Invoice Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Customer Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {isEditing ? (
              <>
                <div className="space-y-2">
                  <Label>Customer Name</Label>
                  <Input 
                    value={editForm.customer_name} 
                    onChange={(e) => setEditForm({ ...editForm, customer_name: e.target.value })}
                    data-testid="edit-customer-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input 
                    type="email"
                    value={editForm.customer_email} 
                    onChange={(e) => setEditForm({ ...editForm, customer_email: e.target.value })}
                    data-testid="edit-customer-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input 
                    value={editForm.customer_phone} 
                    onChange={(e) => setEditForm({ ...editForm, customer_phone: e.target.value })}
                    data-testid="edit-customer-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Address</Label>
                  <Textarea 
                    value={editForm.customer_address} 
                    onChange={(e) => setEditForm({ ...editForm, customer_address: e.target.value })}
                    rows={2}
                    data-testid="edit-customer-address"
                  />
                </div>
                <div className="space-y-2">
                  <Label>GSTIN</Label>
                  <Input 
                    value={editForm.customer_gstin} 
                    onChange={(e) => setEditForm({ ...editForm, customer_gstin: e.target.value })}
                    data-testid="edit-customer-gstin"
                  />
                </div>
              </>
            ) : (
              <>
                <p><strong>Name:</strong> {invoice.customer_name}</p>
                <p><strong>Email:</strong> {invoice.customer_email}</p>
                {invoice.customer_phone && <p><strong>Phone:</strong> {invoice.customer_phone}</p>}
                {invoice.customer_address && <p><strong>Address:</strong> {invoice.customer_address}</p>}
                {invoice.customer_gstin && <p><strong>GSTIN:</strong> {invoice.customer_gstin}</p>}
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Invoice Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {isEditing ? (
              <>
                <div className="space-y-2">
                  <Label>Invoice Date</Label>
                  <Input 
                    type="date"
                    value={editForm.invoice_date} 
                    onChange={(e) => setEditForm({ ...editForm, invoice_date: e.target.value })}
                    data-testid="edit-invoice-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select value={editForm.currency} onValueChange={(v) => setEditForm({ ...editForm, currency: v })}>
                    <SelectTrigger data-testid="edit-currency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="INR">INR (₹)</SelectItem>
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="EUR">EUR (€)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              <>
                <p><strong>Invoice Number:</strong> {invoice.invoice_number}</p>
                <p><strong>Invoice Date:</strong> {invoice.invoice_date?.slice(0, 10)}</p>
                <p><strong>Due Date:</strong> {invoice.due_date?.slice(0, 10)}</p>
                <p><strong>Currency:</strong> {invoice.currency}</p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Line Items */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Line Items</CardTitle>
          {isEditing && (
            <Button size="sm" variant="outline" onClick={handleAddItem} data-testid="add-item-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Item
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-2">Description</th>
                  <th className="text-right py-2 px-2 w-24">Qty</th>
                  <th className="text-right py-2 px-2 w-32">Rate</th>
                  <th className="text-right py-2 px-2 w-24">Tax %</th>
                  <th className="text-right py-2 px-2 w-32">Amount</th>
                  {isEditing && <th className="w-12"></th>}
                </tr>
              </thead>
              <tbody>
                {(isEditing ? editForm.items : invoice.items)?.map((item, index) => (
                  <tr key={index} className="border-b">
                    <td className="py-2 px-2">
                      {isEditing ? (
                        <Input 
                          value={item.description} 
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          placeholder="Item description"
                          data-testid={`item-desc-${index}`}
                        />
                      ) : (
                        item.description
                      )}
                    </td>
                    <td className="text-right py-2 px-2">
                      {isEditing ? (
                        <Input 
                          type="number"
                          value={item.quantity} 
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          className="w-20 text-right"
                          data-testid={`item-qty-${index}`}
                        />
                      ) : (
                        item.quantity
                      )}
                    </td>
                    <td className="text-right py-2 px-2">
                      {isEditing ? (
                        <Input 
                          type="number"
                          value={item.unit_price} 
                          onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                          className="w-28 text-right"
                          data-testid={`item-rate-${index}`}
                        />
                      ) : (
                        `₹${item.unit_price?.toLocaleString()}`
                      )}
                    </td>
                    <td className="text-right py-2 px-2">
                      {isEditing ? (
                        <Input 
                          type="number"
                          value={item.tax_rate || 0} 
                          onChange={(e) => handleItemChange(index, 'tax_rate', e.target.value)}
                          className="w-20 text-right"
                          data-testid={`item-tax-${index}`}
                        />
                      ) : (
                        `${item.tax_rate || 0}%`
                      )}
                    </td>
                    <td className="text-right py-2 px-2 font-medium">
                      ₹{((item.quantity * item.unit_price) || item.amount || 0).toLocaleString()}
                    </td>
                    {isEditing && (
                      <td className="py-2 px-2">
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="h-8 w-8 text-destructive"
                          onClick={() => handleRemoveItem(index)}
                          data-testid={`remove-item-${index}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={isEditing ? 4 : 3} className="text-right py-2 px-2 font-medium">Subtotal:</td>
                  <td className="text-right py-2 px-2 font-medium">₹{(isEditing ? editSubtotal : invoice.subtotal)?.toLocaleString()}</td>
                  {isEditing && <td></td>}
                </tr>
                {/* Show GST breakdown - CGST/SGST for intra-state, IGST for inter-state */}
                {(invoice.is_interstate || invoice.igst > 0) ? (
                  <tr>
                    <td colSpan={isEditing ? 4 : 3} className="text-right py-2 px-2 font-medium">IGST:</td>
                    <td className="text-right py-2 px-2 font-medium">
                      ₹{(isEditing ? editTaxAmount : (invoice.igst || invoice.total_tax || 0))?.toLocaleString()} (18%)
                    </td>
                    {isEditing && <td></td>}
                  </tr>
                ) : (
                  <>
                    <tr>
                      <td colSpan={isEditing ? 4 : 3} className="text-right py-2 px-2 font-medium">CGST:</td>
                      <td className="text-right py-2 px-2 font-medium">
                        ₹{(isEditing ? (editTaxAmount / 2) : (invoice.cgst || (invoice.total_tax || 0) / 2))?.toLocaleString()} (9%)
                      </td>
                      {isEditing && <td></td>}
                    </tr>
                    <tr>
                      <td colSpan={isEditing ? 4 : 3} className="text-right py-2 px-2 font-medium">SGST:</td>
                      <td className="text-right py-2 px-2 font-medium">
                        ₹{(isEditing ? (editTaxAmount / 2) : (invoice.sgst || (invoice.total_tax || 0) / 2))?.toLocaleString()} (9%)
                      </td>
                      {isEditing && <td></td>}
                    </tr>
                  </>
                )}
                <tr className="border-t-2">
                  <td colSpan={isEditing ? 4 : 3} className="text-right py-2 px-2 font-bold text-lg">Total:</td>
                  <td className="text-right py-2 px-2 font-bold text-lg">₹{(isEditing ? editTotal : invoice.total_amount)?.toLocaleString()}</td>
                  {isEditing && <td></td>}
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Notes & Terms */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Notes</CardTitle>
          </CardHeader>
          <CardContent>
            {isEditing ? (
              <Textarea 
                value={editForm.notes} 
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                rows={3}
                placeholder="Additional notes..."
                data-testid="edit-notes"
              />
            ) : (
              <p className="text-muted-foreground">{invoice.notes || 'No notes'}</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Terms & Conditions</CardTitle>
          </CardHeader>
          <CardContent>
            {isEditing ? (
              <Textarea 
                value={editForm.terms} 
                onChange={(e) => setEditForm({ ...editForm, terms: e.target.value })}
                rows={3}
                placeholder="Payment terms..."
                data-testid="edit-terms"
              />
            ) : (
              <p className="text-muted-foreground">{invoice.terms || 'Standard terms apply'}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Payment History */}
      {invoice.payments?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Payment History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Date</th>
                    <th className="text-left py-2">Method</th>
                    <th className="text-left py-2">Reference</th>
                    <th className="text-right py-2">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.payments.map((payment, index) => (
                    <tr key={index} className="border-b">
                      <td className="py-2">{payment.payment_date?.slice(0, 10)}</td>
                      <td className="py-2">{payment.payment_method}</td>
                      <td className="py-2">{payment.transaction_reference || '-'}</td>
                      <td className="text-right py-2 font-medium text-green-600">₹{payment.amount?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Send Invoice Dialog */}
      <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Invoice</DialogTitle>
            <DialogDescription>
              This will email the invoice to {invoice.customer_email}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSendInvoice} disabled={sending}>
              {sending ? 'Sending...' : 'Send Invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Amount (Balance Due: ₹{invoice.balance_due?.toLocaleString()})</Label>
              <Input
                type="number"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({ ...paymentData, amount: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select value={paymentData.payment_method} onValueChange={(v) => setPaymentData({ ...paymentData, payment_method: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="razorpay">Razorpay</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Transaction Reference</Label>
              <Input
                value={paymentData.transaction_reference}
                onChange={(e) => setPaymentData({ ...paymentData, transaction_reference: e.target.value })}
                placeholder="UTR / Cheque No. / Reference"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={paymentData.notes}
                onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleRecordPayment} disabled={processing}>
              {processing ? 'Recording...' : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Invoice Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Invoice</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this invoice? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Reason (optional)</Label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Reason for cancellation"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>Keep Invoice</Button>
            <Button variant="destructive" onClick={handleCancelInvoice} disabled={processing}>
              {processing ? 'Cancelling...' : 'Cancel Invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
